import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatBRL, formatBRLouTraco, formatDateTimeBR } from "../../lib/api";
import { Search, Filter, TrendingUp, Banknote, Clock, FileText, Download, Eye, FileDown, Plus } from "lucide-react";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "../../contexts/AuthContext";
import { cedentePath } from "../../lib/portal";
import useCedenteNewOperationGate from "../../hooks/useCedenteNewOperationGate";
import { exportCSV, exportPDF } from "../../lib/export";
import DateRangeFilter, { isInRange } from "../../components/DateRangeFilter";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";

const STATUSES = ["pendente", "aprovada", "liquidada", "recusada", "cancelada"];

const EXPORT_COLUMNS = [
  { key: "numero", label: "Número" },
  { key: "product_nome", label: "Produto" },
  { key: "product_tipo", label: "Tipo" },
  // Item 15: NULL = "a V2 não devolveu o líquido". `formatBRL` fabricaria
  // "R$ 0,00" ANTES do `cellValue` do export poder ver o null — o traço tem
  // de sair daqui, do `format` da coluna.
  { key: "valor_liquido", label: "Valor Líquido", format: (v) => formatBRLouTraco(v) },
  { key: "num_notas", label: "Nº Notas", format: (v, r) => v || Math.ceil(r.valor / 50000) || 1 },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Criada em", format: (v) => formatDateTimeBR(v) },
];

function MiniKpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="zemo-card p-4 zemo-fade-up">
      <div className="flex items-center justify-between">
        <span className="zemo-overline">{label}</span>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent || "bg-zinc-50 border border-[var(--zemo-border)] text-zinc-700"}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight font-mono-num text-zinc-900">{value}</div>
    </div>
  );
}

function DocPlaceholder({ title, icon: Icon }) {
  return (
    <div className="flex items-center justify-between p-3 border border-[var(--zemo-border)] rounded-md bg-zinc-50/50">
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-zinc-500" />
        <span className="text-sm text-zinc-700">{title}</span>
      </div>
      <Button variant="ghost" size="sm" className="text-xs text-zinc-500" disabled>
        <Download className="h-3.5 w-3.5 mr-1" /> Em breve
      </Button>
    </div>
  );
}

export default function CedenteOperations() {
  const { user } = useAuth();
  const canEdit = user?.role === "cedente_admin" || user?.role === "cedente_operator";
  // Gate de entrada (04(a)): flag de build + catálogo com produto habilitado.
  const { visible: newOperationVisible } = useCedenteNewOperationGate();
  const [ops, setOps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [opsRes, sumRes] = await Promise.all([
      api.get("/cedente/operations"),
      api.get("/cedente/dashboard/summary"),
    ]);
    setOps(opsRes.data);
    setSummary(sumRes.data);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return ops.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if ((dateFrom || dateTo) && !isInRange(o.created_at, dateFrom, dateTo)) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!o.numero.toLowerCase().includes(s) && !o.product_nome.toLowerCase().includes(s))
          return false;
      }
      return true;
    });
  }, [ops, search, statusFilter, dateFrom, dateTo]);

  return (
    <div className="space-y-6" data-testid="cedente-operations-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Operações</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">
            Suas Antecipações &amp; Crédito
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Acompanhe todas as suas operações de antecipação.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-10"
            disabled={filtered.length === 0}
            onClick={() => exportCSV("minhas-operacoes-zemo", EXPORT_COLUMNS, filtered)}
            data-testid="ced-export-csv-btn"
          >
            <FileDown className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button
            variant="outline"
            className="h-10"
            disabled={filtered.length === 0}
            onClick={() => exportPDF("Relatório de Operações — Cedente", "minhas-operacoes-zemo", EXPORT_COLUMNS, filtered)}
            data-testid="ced-export-pdf-btn"
          >
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          {/* Molde: pages/Operations.jsx:129-136 (botão primário do parceiro).
              Duplo gate: papel de editor E gate de entrada da feature. */}
          {canEdit && newOperationVisible && (
            <Link to={cedentePath("/operacoes/nova")} data-testid="ced-new-operation-link">
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-10 font-semibold">
                <Plus className="h-4 w-4 mr-1.5" /> Nova Operação
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Mini KPIs */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniKpi label="Total de Operações" value={summary.total_operacoes} icon={TrendingUp} />
          <MiniKpi label="Volume Antecipado" value={formatBRL(summary.volume_total)} icon={Banknote}
                   accent="bg-emerald-50 border border-emerald-200 text-emerald-700" />
          <MiniKpi label="Volume Liquidado" value={formatBRL(summary.volume_liquidado)} icon={Banknote} />
          <MiniKpi label="Pendentes" value={summary.operacoes_pendentes} icon={Clock}
                   accent="bg-amber-50 border border-amber-200 text-amber-700" />
        </div>
      )}

      {/* Filters */}
      <div className="zemo-card p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Buscar por número ou produto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
            data-testid="ced-ops-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-zinc-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] h-10" data-testid="ced-ops-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:ml-auto">
          <DateRangeFilter
            from={dateFrom}
            to={dateTo}
            onChange={({ from, to }) => {
              if (from !== undefined) setDateFrom(from);
              if (to !== undefined) setDateTo(to);
            }}
            testidPrefix="ced-ops"
          />
        </div>
      </div>

      {/* Table */}
      <div className="zemo-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Número</th>
                <th className="text-left px-5 py-3 font-semibold">Produto</th>
                <th className="text-right px-5 py-3 font-semibold">Valor Líquido</th>
                <th className="text-right px-5 py-3 font-semibold">Nº Notas</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Criada em</th>
                <th className="text-center px-5 py-3 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--zemo-border)]">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-zinc-500">
                    Nenhuma operação encontrada.
                  </td>
                </tr>
              )}
              {filtered.map((op) => (
                <tr
                  key={op.id}
                  className="hover:bg-zinc-50/60 transition-colors"
                  data-testid={`ced-op-row-${op.numero}`}
                >
                  <td className="px-5 py-3.5 font-mono-num font-semibold text-zinc-900">{op.numero}</td>
                  <td className="px-5 py-3.5">
                    <div className="text-zinc-800">{op.product_nome}</div>
                    <div className="text-[11px] text-zinc-500 capitalize">{op.product_tipo}</div>
                  </td>
                  <td className="px-5 py-3.5 text-right font-semibold text-zinc-900 font-mono-num">
                    {formatBRLouTraco(op.valor_liquido)}
                  </td>
                  <td className="px-5 py-3.5 text-right text-zinc-700 font-mono-num">
                    {op.num_notas || Math.ceil(op.valor / 50000) || 1}
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusBadge status={op.status} />
                  </td>
                  <td className="px-5 py-3.5 text-[12px] text-zinc-500">{formatDateTimeBR(op.created_at)}</td>
                  <td className="px-5 py-3.5 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#0055FF] hover:text-[#0044CC] hover:bg-blue-50"
                      onClick={() => setSelected(op)}
                      data-testid={`ced-op-detail-${op.numero}`}
                    >
                      <Eye className="h-4 w-4 mr-1" /> Detalhes
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="ced-op-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-xl font-mono-num">{selected?.numero}</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <StatusBadge status={selected.status} />

              <div className="grid grid-cols-2 gap-3">
                {[
                  ["Produto", selected.product_nome],
                  ["Tipo", selected.product_tipo],
                  ["Valor Líquido", formatBRLouTraco(selected.valor_liquido)],
                  ["Nº Notas", selected.num_notas || Math.ceil(selected.valor / 50000) || 1],
                  ["Criada em", formatDateTimeBR(selected.created_at)],
                  ["Criada por", selected.created_by_name],
                ].map(([k, v]) => (
                  <div key={k} className="p-3 border border-[var(--zemo-border)] rounded-md">
                    <div className="zemo-overline">{k}</div>
                    <div className="mt-1 font-semibold text-zinc-900 text-sm capitalize">{v || "—"}</div>
                  </div>
                ))}
              </div>

              {/* Documentos */}
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 mb-3">Documentos</h3>
                <div className="space-y-2">
                  <DocPlaceholder title="Comprovante de Pagamento" icon={FileText} />
                  <DocPlaceholder title="Contrato da Operação" icon={FileText} />
                  <DocPlaceholder title="NFs / Lastro" icon={FileText} />
                </div>
              </div>

              {selected.descricao && (
                <div className="p-3 border border-[var(--zemo-border)] rounded-md">
                  <div className="zemo-overline">Descrição</div>
                  <div className="mt-1 text-sm text-zinc-700">{selected.descricao}</div>
                </div>
              )}

              <div className="rounded-md p-3 border border-emerald-200 bg-emerald-50 text-[12px] text-emerald-900 flex items-start gap-2">
                <FileText className="h-4 w-4 mt-0.5" />
                <div>
                  Documentos serão disponibilizados automaticamente quando a operação for processada pelo parceiro.
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
